import { Fragment, useState } from 'react';
import { Streak } from '../components/Brand';
import { api } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { useQuery } from '../lib/query';
import { Link } from '../lib/router';
import { useApp } from '../lib/store';
import { Icon } from '../components/Icon';
import { PosterArt } from '../components/Poster';
import { ContinueRail, Rail, Top10Rail } from '../components/Rails';
import { AppShell, ErrorBox, SkeletonRails } from '../components/Shell';
import type { HomeSection, Show } from '../lib/types';

function Featured({ items }: { items: Show[] }) {
  const { topics: TOPICS } = useCatalog();
  return (
    <section className="featured" aria-label="Featured">
      {items.map((s, i) => (
        <div key={s.id} className="featured-card">
          <PosterArt show={s} size="fill">
            <span className="featured-overlay">
              <span className="featured-meta">{TOPICS.find((t) => t.slug === s.topic)?.name} · {s.episodeCount} episodes</span>
              <span className="featured-desc">{s.description}</span>
              <span className="row-wrap">
                <Link to={`/watch/${s.id}/1`} className="btn btn-primary"><Icon name="play" size={14} /> {i === 0 ? 'Watch free' : 'Watch Ep 1'}</Link>
                <Link to={`/show/${s.slug}`} className="btn btn-glass">Episodes</Link>
              </span>
            </span>
          </PosterArt>
        </div>
      ))}
    </section>
  );
}

function Section({ s }: { s: HomeSection }) {
  switch (s.kind) {
    case 'featured': return <Featured items={s.items} />;
    case 'continue': return <ContinueRail items={s.items} />;
    case 'top10': return <Top10Rail title={s.title} subtitle={s.subtitle} items={s.items} />;
    default: return <Rail title={s.title} subtitle={s.subtitle} items={s.items} to="/explore" />;
  }
}

export function Home() {
  const { topics: TOPICS } = useCatalog();
  const progressKey = useApp((s) => Object.keys(s.progress).length + ':' + s.interests.join(','));
  const session = useApp((s) => s.session);
  const { data, error, loading, reload } = useQuery('home:' + progressKey, () => api.home());
  const [chip, setChip] = useState('all');
  const filtered = (data || []).map((s) =>
    chip === 'all' || s.kind === 'continue' || s.kind === 'featured'
      ? s
      : { ...s, items: (s.items as Show[]).filter((x) => x.topic === chip) } as HomeSection
  ).filter((s) => s.items.length > 0);

  return (
    <AppShell>
      <div className="page home">
        <nav className="chips" aria-label="Topics">
          {[{ slug: 'all', name: 'All' }, ...TOPICS].map((t) => (
            <button key={t.slug} type="button" className={'chip' + (chip === t.slug ? ' on' : '')} aria-pressed={chip === t.slug} onClick={() => setChip(t.slug)}>
              {t.name}
            </button>
          ))}
        </nav>
        {loading && !data && <SkeletonRails rails={3} />}
        {error && <ErrorBox message={error} onRetry={reload} />}
        {filtered.map((s, i) => (
          <Fragment key={s.kind + i + s.title}>
            <Section s={s} />
            {i === 0 && session && <Streak />}
          </Fragment>
        ))}
      </div>
    </AppShell>
  );
}
